import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api } from "../lib/api";
import { CaseStudyCard } from "../components/home/CaseStudiesSection";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export default function CaseStudies() {
  const [cases, setCases] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/case-studies").then((res) => setCases(res.data)).catch(() => {});
  }, []);

  return (
    <div className="pt-32 lg:pt-40 pb-24" data-testid="case-studies-page">
      <div className="container-x">
        <p className="section-label">CASE STUDIES</p>
        <h1 className="mt-5 font-heading text-5xl sm:text-6xl font-black tracking-tight text-white max-w-3xl">
          PROBLEMS IN. <span className="text-flame">SOFTWARE OUT.</span>
        </h1>
        <p className="mt-6 text-slate-400 max-w-2xl leading-relaxed">
          Every entry below is a clearly-labelled demo/concept project showing realistic scope, hours and timelines.
          We never fabricate clients, testimonials or results — genuine case studies are published only with client consent.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cases.map((cs) => (
            <button key={cs.case_id} onClick={() => setActive(cs)} className="text-left" data-testid={`case-open-${cs.case_id}`}>
              <CaseStudyCard cs={cs} />
            </button>
          ))}
        </div>
        {!cases.length && <p className="mt-16 text-slate-500 text-sm">No case studies published yet.</p>}

        <div className="mt-20 rounded-2xl border border-flame/30 bg-navy-800 p-10 sm:p-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">YOUR PROJECT COULD BE NEXT.</h2>
          <Link to="/get-estimate" data-testid="cases-estimate-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-flame hover:bg-flame-hover text-white font-heading font-bold px-8 py-4 transition-colors">
            GET MY ESTIMATE <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent className="bg-navy-800 border-white/10 max-w-2xl max-h-[85vh] overflow-y-auto" aria-describedby={undefined}>
          {active && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">{active.title}</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 text-sm" data-testid="case-detail-dialog">
                <p className="font-mono text-[10px] tracking-[0.25em] text-flame">{active.client_label} · {active.delivery_model}</p>
                <div>
                  <p className="section-label !text-slate-500 mb-2">PROBLEM</p>
                  <p className="text-slate-300 leading-relaxed">{active.problem}</p>
                </div>
                <div>
                  <p className="section-label !text-slate-500 mb-2">REQUIREMENT</p>
                  <p className="text-slate-300 leading-relaxed">{active.requirement}</p>
                </div>
                <div className="grid grid-cols-3 gap-4 font-mono text-xs">
                  <div className="rounded-lg bg-navy-900/70 p-4"><p className="text-slate-500 text-[9px] tracking-widest">TYPE</p><p className="mt-1 text-slate-200">{active.application_type}</p></div>
                  <div className="rounded-lg bg-navy-900/70 p-4"><p className="text-slate-500 text-[9px] tracking-widest">HOURS</p><p className="mt-1 text-slate-200">{active.estimated_hours}</p></div>
                  <div className="rounded-lg bg-navy-900/70 p-4"><p className="text-slate-500 text-[9px] tracking-widest">TIMELINE</p><p className="mt-1 text-slate-200">{active.timeline}</p></div>
                </div>
                <div>
                  <p className="section-label !text-slate-500 mb-3">FEATURES</p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(active.features || []).map((f) => (
                      <li key={f} className="flex items-center gap-2 text-xs text-slate-300">
                        <Check className="w-3.5 h-3.5 text-flame shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="section-label !text-slate-500 mb-2">OUTCOME</p>
                  <p className="text-slate-300 leading-relaxed">{active.result}</p>
                </div>
                <p className="text-[11px] italic text-slate-500 border-l-2 border-flame/40 pl-4">{active.testimonial}</p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
